import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Plus, Trash2 } from 'lucide-react'
import { ExceptionTimeCard } from './exception-time-card'
import { GoodbyeCard } from './goodbye-card'
import { GreetingCard } from './greeting-card'
import { RoutingTargetCard } from './routing-target-card'

interface ExceptionCardProps {
    exceptionObject: {
        validFrom?: string
        validTo?: string
        validOnDay?: string[]
        utteranceToSpeak?: string
        utteranceLanguage?: string
        utteranceAtEnd?: string
        possibleSelections?: Record<string, any>
    }
    onUpdateTime: (data: any) => void
    onUpdateGreeting: (data: any) => void
    onUpdateGoodbye: (data: any) => void
    onUpdateTarget: (data: any) => void
    onDeleteTarget: (targetIndex: number, targetNumber: string) => void
    onAddTarget: () => void
    onDeleteException: () => void
}

export function ExceptionCard({
    exceptionObject,
    onUpdateTime,
    onUpdateGreeting,
    onUpdateGoodbye,
    onUpdateTarget,
    onDeleteTarget,
    onAddTarget,
    onDeleteException
}: ExceptionCardProps) {
    const routingTargets = exceptionObject.possibleSelections
        ? Object.entries(exceptionObject.possibleSelections)
        : []

    // Extract values with defaults
    const validFrom = exceptionObject.validFrom ?? '00:00'
    const validTo = exceptionObject.validTo ?? '23:59'
    const validOnDay = exceptionObject.validOnDay ?? []
    const utteranceToSpeak = exceptionObject.utteranceToSpeak ?? ''
    const utteranceLanguage = exceptionObject.utteranceLanguage ?? 'de'
    const utteranceAtEnd = exceptionObject.utteranceAtEnd ?? ''

    return (
        <Card className="border border-gray-200 shadow-sm">
            {/* Time Configuration */}
            <CardHeader className="bg-gray-50 py-3">
                <ExceptionTimeCard
                    validFrom={validFrom}
                    validTo={validTo}
                    validOnDay={validOnDay}
                    onUpdate={onUpdateTime}
                />
            </CardHeader>

            {/* Greeting */}
            <CardContent className="py-4 px-4 bg-white border-t border-gray-100">
                <div className="space-y-2">
                    <h4 className="text-xs text-gray-500 font-medium">Begrüßungsnachricht</h4>
                    <GreetingCard
                        title="Begrüßung"
                        message={utteranceToSpeak}
                        language={utteranceLanguage}
                        onUpdate={onUpdateGreeting}
                    />
                </div>
            </CardContent>

            {/* Routing Targets */}
            <CardContent className="py-4 px-4 bg-white border-t border-gray-100">
                <div className="space-y-3">
                    <h4 className="text-xs text-gray-500 font-medium">Weiterleitungsziele</h4>

                    {routingTargets.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-md">
                            Keine Weiterleitungsziele konfiguriert
                        </div>
                    ) : (
                        routingTargets.map(([targetNumber, targetObject]: [string, any], targetIndex) => (
                            <RoutingTargetCard
                                key={targetNumber}
                                index={targetIndex}
                                callRoutingTargetNumber={targetNumber}
                                routingTargetObject={targetObject}
                                onUpdate={onUpdateTarget}
                                onDelete={() => onDeleteTarget(targetIndex, targetNumber)}
                            />
                        ))
                    )}

                    {/* Add Target Button */}
                    <button
                        onClick={onAddTarget}
                        className="w-full border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors flex items-center justify-center"
                    >
                        <div className="bg-white p-1.5 rounded-full border border-gray-100">
                            <Plus className="h-4 w-4 text-brand-primary" />
                        </div>
                    </button>
                </div>
            </CardContent>

            {/* Goodbye */}
            <CardContent className="py-4 px-4 bg-white border-t border-gray-100">
                <div className="space-y-2">
                    <h4 className="text-xs text-gray-500 font-medium">Abschiedsnachricht</h4>
                    <GoodbyeCard
                        title="Abschied"
                        message={utteranceAtEnd}
                        language={utteranceLanguage}
                        onUpdate={onUpdateGoodbye}
                    />
                </div>
            </CardContent>

            {/* Footer with Delete Button */}
            <CardFooter className="bg-gray-50 border-t border-gray-100 py-3 px-4 flex justify-end">
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={onDeleteException}
                    className="bg-red-500 hover:bg-red-600 text-white"
                >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Exception löschen
                </Button>
            </CardFooter>
        </Card>
    )
}
